import { ThermalProgressResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/thermal-progress.response';
import { addLocalDays, parseLocalDate, startOfLocalDay } from './local-date.util';

/**
 * Ce que l'horloge d'une étape lit d'une plante : la fiche (`GET /plants/:uid`),
 * une liste de plantes complétée par la progression thermique, ou une ligne du
 * suivi de croissance de l'accueil (`stepProgress` en guise de progression).
 */
export interface StepClockSource {
  currentGrowingStep?: { durationDays?: number | null; isBlocking?: boolean | null } | null;
  /** Début de l'étape, « 2026-05-01 » comme l'envoie l'API. */
  currentGrowingStepStartDate?: Date | string | null;
  /** Avancement calendaire calculé par l'API, quand le début ou la durée manquent ici. */
  currentGrowingStepProgression?: number | null;
  thermalProgress?: Partial<ThermalProgressResponse> | null;
}

/**
 * Écart à la normale au-delà duquel la fiche le dit : 15 %. En dessous, la
 * différence tient à la météo d'une semaine ordinaire et ne mérite pas de phrase.
 */
export const SPEED_TREND_THRESHOLD = 0.15;

/** Vitesse de la normale sous laquelle un rapport n'a plus de sens (saison de pause). */
const MIN_NORMAL_SPEED = 0.05;

export interface StepClock {
  /** `thermal` : la chaleur reçue fait foi ; `calendar` : le début plus la durée prévue. */
  mode: 'thermal' | 'calendar';
  /** Avancement de l'étape, de 0 à 1. */
  progress: number;
  /** Étape finie selon son horloge, sans passage à la suivante : « en retard ». */
  overdue: boolean;
  /** Plante cyclique : étape finie, la suivante attend sa fenêtre saisonnière. */
  waitingForSeason: boolean;
  /**
   * Fin de l'étape : estimée par la chaleur reçue, ou début plus durée (au
   * passé quand l'étape est en retard). `null` quand l'horloge ne la connaît pas.
   */
  endDate: Date | null;
  /** Jours d'affilée sans météo, non comptés (0 : météo à jour, ou étape calendaire). */
  weatherGapDays: number;
  /**
   * La semaine passée face à la normale du lieu : plus vite ou plus lentement
   * que d'habitude, `null` dans l'écart ordinaire ou sans données.
   */
  speedTrend: 'faster' | 'slower' | null;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * L'horloge d'une étape, la même sur tous les écrans.
 *
 * Une étape qui accumule de la chaleur (cible en degrés-jours connue) suit
 * cette accumulation : barre, retard (cumul ≥ cible) et fin estimée. Un
 * printemps froid la garde en cours au-delà de sa durée calendaire, sans
 * qu'elle soit « en retard ». Le calendrier (début plus durée) ne sert qu'aux
 * étapes sans données thermiques.
 */
export function stepClock(source: StepClockSource | null | undefined, now: Date = new Date()): StepClock {
  const thermal = source?.thermalProgress;
  if (thermal && typeof thermal.cumTu === 'number' && typeof thermal.targetTu === 'number' && thermal.targetTu > 0) {
    // `stepComplete` (F6) dit si la cible est atteinte ; une réponse plus ancienne le laisse déduire.
    const reached = thermal.stepComplete ?? thermal.cumTu >= thermal.targetTu;
    const waitingForSeason = reached && !!thermal.waitingForSeason;
    // Sans météo, la date glisserait chaque jour : pas de fin estimée, et pas de repli calendaire.
    const missingData = thermal.conditionBadge === 'MISSING_DATA';
    const daysLeft = thermal.estimatedDaysLeft;
    return {
      mode: 'thermal',
      progress: reached ? 1 : clamp01(thermal.cumTu / thermal.targetTu),
      overdue: reached && !waitingForSeason,
      waitingForSeason,
      endDate: !reached && !missingData && typeof daysLeft === 'number' ? estimatedEndDate(daysLeft, now) : null,
      weatherGapDays: thermal.weatherGapDays ?? 0,
      speedTrend: missingData || waitingForSeason || (thermal.weatherGapDays ?? 0) > 0 ? null : speedTrend(thermal.recentSpeed, thermal.normalSpeed),
    };
  }

  const start = parseLocalDate(source?.currentGrowingStepStartDate);
  const duration = source?.currentGrowingStep?.durationDays;
  const end = start && duration && duration > 0 ? addLocalDays(start, duration) : null;
  const progress = start && end
    ? (now.getTime() - start.getTime()) / (end.getTime() - start.getTime())
    : source?.currentGrowingStepProgression ?? 0;
  return {
    mode: 'calendar',
    progress: clamp01(progress),
    overdue: progress >= 1,
    waitingForSeason: false,
    endDate: end,
    weatherGapDays: 0,
    speedTrend: null,
  };
}

/** Vitesse des 7 derniers jours face à celle de la normale, au-delà de ± `SPEED_TREND_THRESHOLD`. */
function speedTrend(recent: number | null | undefined, normal: number | null | undefined): 'faster' | 'slower' | null {
  if (typeof recent !== 'number' || typeof normal !== 'number' || normal < MIN_NORMAL_SPEED) return null;
  const ratio = recent / normal;
  if (ratio >= 1 + SPEED_TREND_THRESHOLD) return 'faster';
  if (ratio <= 1 - SPEED_TREND_THRESHOLD) return 'slower';
  return null;
}

/**
 * « Cette plante attend une action » : étape manuelle (bloquante, jamais
 * franchie automatiquement) finie selon son horloge. Les badges de Mon Potager
 * et le bandeau « C'est fait ? » de la fiche en dépendent.
 */
export function stepNeedsAction(source: StepClockSource | null | undefined, now: Date = new Date()): boolean {
  return !!source?.currentGrowingStep?.isBlocking && stepClock(source, now).overdue;
}

/**
 * Fin estimée à partir d'un nombre de jours restants : aujourd'hui plus ce
 * nombre arrondi au jour, pour que l'accueil et la fiche donnent la même date.
 */
export function estimatedEndDate(daysLeft: number, now: Date = new Date()): Date {
  return addLocalDays(startOfLocalDay(now), Math.round(daysLeft));
}
